import { Value } from 'baseui/select';
import { useFormState } from 'components/elements/form';
import useActivityColumn from 'components/elements/table/editable-columns/activity-column';
import deleteColumn from 'components/elements/table/editable-columns/delete-column';
import {
  ActionType,
  ColumnDataType,
  IColumn,
} from 'components/elements/table/type';
import useTranslation from 'next-translate/useTranslation';
import React from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';

import { addNewGuestDetail } from '../../hooks/use-get-default-values';
import {
  ActivityPurchaseFormType,
  ActivityPurchaseGuestDetailsFormType,
} from '../form-type';

export function useGetGroupDetailTableList(
  isGroupedPrice: boolean = false,
): IColumn<ActivityPurchaseGuestDetailsFormType>[] {
  const { t } = useTranslation();
  const { editable } = useFormState();
  const activityColumn = useActivityColumn();

  const columns = React.useMemo<
    IColumn<ActivityPurchaseGuestDetailsFormType>[]
  >(
    () => [
      {
        header: t('common:other'),
        accessorKey: 'activityId',
        minSize: 250,
        maxSize: 250,
        dataType: ColumnDataType.select,
        selectProps: activityColumn,
        required: true,
      },
      {
        header: t('common:type'),
        accessorKey: 'type',
        minSize: 150,
        maxSize: 150,
        dataType: ColumnDataType.null,
        disabled: true,
      },
      {
        header: t('common:date'),
        accessorKey: 'date',
        minSize: 150,
        maxSize: 150,
        dataType: ColumnDataType.date,
      },
      {
        header: t('common:price'),
        accessorKey: 'price',
        minSize: 150,
        maxSize: 150,
        dataType: ColumnDataType.number,
        minNumber: 0,
        required: true,
      },
      {
        header: t(isGroupedPrice ? 'common:total_qty' : 'common:qty'),
        accessorKey: 'qty',
        minSize: 150,
        maxSize: 150,
        dataType: ColumnDataType.number,
        minNumber: 0,
        required: true,
      },
      {
        header: t('common:total_price'),
        accessorKey: 'totalPrice',
        minSize: 150,
        maxSize: 150,
        dataType: ColumnDataType.number,
        minNumber: 0,
        disabled: true,
      },
      {
        header: t('common:description'),
        accessorKey: 'description',
        minSize: 150,
        maxSize: 150,
        dataType: ColumnDataType.text,
      },
      ...deleteColumn(editable),
    ],
    [activityColumn, editable, isGroupedPrice, t],
  );

  return columns;
}

export function useGetGroupDetailDispatch(parentIndex = 0, guestIndex = 0) {
  const { clearErrors, getValues, control, trigger, setValue } =
    useFormContext<ActivityPurchaseFormType>();

  const parentName = `activityPurchases.${parentIndex}` as const;
  const name =
    `activityPurchases.${parentIndex}.activityPurchaseGuests.${guestIndex}.activityPurchaseGuestDetails` as const;

  const details = useFieldArray({
    control,
    name,
    keyName: '__customId',
  });

  const { append, remove, update } = details;

  const { isGroupedPrice, activityPurchaseGuests } = getValues(parentName);

  const dispatch = React.useCallback(
    (action) => {
      const details = getValues(name);
      const currentRowIndex = action.rowIndex as number;
      const detail = details[currentRowIndex];
      const columnId =
        action.columnId as keyof ActivityPurchaseGuestDetailsFormType;
      const extra = action.extra as Value;
      const type = action.type as ActionType;
      const value = action.value;
      const otherVal: () => Partial<ActivityPurchaseGuestDetailsFormType> =
        () => {
          if (columnId === 'activityId') {
            return {
              activityName: extra?.[0]?.label as string,
              type: extra?.[0]?.extra?.type,
            };
          }
          if (columnId === 'price') {
            return {
              totalPrice: (value || 0) * (detail.qty || 0),
            };
          }
          if (columnId === 'qty') {
            return {
              totalPrice: (value || 0) * (detail.price || 0),
            };
          }
          return {};
        };
      if (type === 'addRow') {
        append(addNewGuestDetail());
        trigger(name);
      } else if (type === 'removeRow') {
        remove(currentRowIndex);
        trigger(name);
      } else if (type === 'update_cell') {
        const item = {
          ...detail,
          [columnId]: value,
          ...otherVal(),
        };
        update(currentRowIndex, {
          ...detail,
          [columnId]: value,
          ...otherVal(),
        });
        clearErrors(`${name}.${currentRowIndex}.${columnId}`);

        if (isGroupedPrice) {
          const guestName = `${parentName}.activityPurchaseGuests` as const;
          const _activityPurchaseGuests = activityPurchaseGuests.map((apg) => {
            const activityPurchaseGuestDetails =
              apg.activityPurchaseGuestDetails.map((apd, index) => {
                if (index === currentRowIndex) {
                  return item;
                }
                return apd;
              });

            return {
              ...apg,
              activityPurchaseGuestDetails,
            };
          });
          setValue(guestName, _activityPurchaseGuests);
          clearErrors(guestName);
        }
      }
    },
    [
      activityPurchaseGuests,
      append,
      clearErrors,
      getValues,
      isGroupedPrice,
      name,
      parentName,
      remove,
      setValue,
      trigger,
      update,
    ],
  );

  return { dispatch, details, name };
}
